import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useTree } from '@/context/TreeContext';
import type { TreeNode } from '@/lib/types';

interface Crumb {
  id: string;
  label: string;
  to?: string; // omitted => rendered as plain text (folders / current node)
}

interface BreadcrumbsProps {
  nodeId: string;
  /** When true, the last crumb (the current node) is rendered as plain text. */
  currentIsLeaf?: boolean;
}

/**
 * Reads the shared tree. It used to fetch /api/tree itself on every nodeId change —
 * a second round-trip AND a second stale copy of every title, so a renamed page kept
 * its old name in the trail. One store, one copy, renames land everywhere at once.
 */
export default function Breadcrumbs({ nodeId, currentIsLeaf = true }: BreadcrumbsProps) {
  const { tree } = useTree();

  const crumbs = useMemo<Crumb[]>(() => {
    if (!tree) return [];
    const byId = new Map(tree.nodes.map((n) => [n.id, n]));
    const node = byId.get(nodeId);
    if (!node) return [];

    // Walk parentId up to the section root.
    const chain: TreeNode[] = [];
    let cur: TreeNode | undefined = node;
    const guard = new Set<string>();
    while (cur && !guard.has(cur.id)) {
      guard.add(cur.id);
      chain.unshift(cur);
      cur = cur.parentId ? byId.get(cur.parentId) : undefined;
    }

    const section = tree.sections.find((s) => s.id === node.sectionId);
    const result: Crumb[] = [];
    if (section) result.push({ id: section.id, label: section.name });

    chain.forEach((n, idx) => {
      const isLast = idx === chain.length - 1;
      // Docs are linkable; folders have no dedicated view route, so leave them as text.
      // The current (last) node is text when currentIsLeaf is set.
      const linkable = n.type === 'doc' && !(isLast && currentIsLeaf);
      result.push({
        id: n.id,
        label: n.title || 'Untitled',
        to: linkable ? `/doc/${n.id}` : undefined,
      });
    });

    return result;
  }, [tree, nodeId, currentIsLeaf]);

  if (crumbs.length === 0) return null;

  return (
    <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1 text-sm text-ink-muted">
      {crumbs.map((c, i) => (
        <span key={c.id} className="flex items-center gap-1">
          {i > 0 && <ChevronRight size={14} className="shrink-0 text-ink-faint" />}
          {c.to ? (
            <Link to={c.to} className="rounded px-1 hover:bg-surface-2 hover:text-ink">
              {c.label}
            </Link>
          ) : (
            <span className={i === crumbs.length - 1 ? 'px-1 text-ink' : 'px-1'}>{c.label}</span>
          )}
        </span>
      ))}
    </nav>
  );
}
